import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {contentMaxWidth, colors, borderRadius, lineHeightRatio} from '@/styles/tokens';
import {cdnImage} from '@/util/cdnImage';
import type {resultsSectionContent} from '@/util/contents/drawpost-data';

interface ResultsSectionProps {
    heading: string;
    content: typeof resultsSectionContent;
}

const sectionSx = {
    bgcolor: colors.background.default
} as const;

const imageWrapSx = {
    overflow: 'hidden',
    borderRadius: borderRadius.xl,
    width: '100%',
    maxWidth: contentMaxWidth.standard,
    mx: 'auto',
    mt: 6
} as const;

const resultsImageStyle = {width: '100%', height: 'auto', display: 'block'} as const;

const ResultsSection = memo(function ResultsSection({heading, content}: ResultsSectionProps) {
    const resultsImage = cdnImage(content.imagePath);

    return (
        <Box className="top-space bottom-spacing mobile-top-padding mobile-padding-bottom" sx={sectionSx}>
            <MUIContainer>
                <Box textAlign="center" maxWidth={contentMaxWidth.standard} margin="0 auto" mb={5}>
                    <Typography
                        variant="h1"
                        component="h2"
                        mb={3}
                        fontSize={{xs: 35, sm: 48}}
                        className="font-headers"
                        sx={{color: colors.text.primary}}
                    >
                        {heading}
                    </Typography>
                    {content.paragraphs.map((text, i) => (
                        <Typography
                            key={i}
                            variant="subtitle1"
                            component="p"
                            mb={3}
                            className="font-regular-text remove-underline"
                            sx={{color: colors.text.primary, lineHeight: lineHeightRatio.looser}}
                        >
                            {text}
                        </Typography>
                    ))}
                </Box>

                <Box sx={imageWrapSx}>
                    <Image
                        src={resultsImage}
                        alt={content.imageAlt}
                        width={1536}
                        height={914}
                        loading="lazy"
                        style={resultsImageStyle}
                        sizes="(max-width: 1280px) 100vw, 1280px"
                    />
                </Box>
            </MUIContainer>
        </Box>
    );
});

export default ResultsSection;
